import { useCallback, useEffect, useRef, useState } from "react";
import {
  generateProductUrl,
  generateAbbreviation,
  getLoggedinUser,
} from "../../../../../lib/util";
import {
  useCartPaymentCharge,
  useUpdateActiveCart,
} from "../../../../../lib/services/collection-service";
import { useGetProduct } from "../../../../../lib/services/product-service";
import { useFormatCurrency } from "../../../../common/format-currency";
import useDebounce from "../../../../common/useDebounce";
import toast from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import { cartIcon } from "../../../../../assets";
import { updateCart, getCartSource } from "../../../../slice/cart-slice";
import { useAppDispatch, useAppSelector } from "../../../../slice/hooks";
import { useHistoryManager } from "../../../../history/useHistoryManager";
import { getActiveUser } from "../../../../slice/auth-slice";

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

export const calculateCartTotal = (cartItems) => {
  if (!cartItems.paymentItem) {
    return 0;
  }
  return cartItems.paymentItem.reduce(
    (acc, product) => acc + product.amount * product.unit,
    0,
  );
};

const getCartOrganisationId = (cart) => {
  return cart?.profileId || cart?.host?.profileId || cart?.host?.orgId;
};

const getCartWithPaymentItems = (cart, paymentItem) => {
  const amount = calculateCartTotal({ paymentItem });
  const previousAmount = Number(cart?.amount ?? 0);
  const previousTotalAmount = Number(cart?.totalAmount ?? previousAmount);
  const totalAmount = Math.max(
    0,
    previousTotalAmount + amount - previousAmount,
  );

  return {
    ...cart,
    amount,
    totalAmount,
    paymentItem,
  };
};

const getCartSourceLabel = (cart) => {
  const hostName = cart?.host?.name || "Store";
  const accessPointName = cart?.accessPointName || "";
  const shouldShowAccessPoint =
    accessPointName.trim().toLowerCase() !== "main entrance";

  return shouldShowAccessPoint && accessPointName
    ? `${hostName} (${accessPointName})`
    : hostName;
};

const getCartHostStorePath = (cart, storePath, navLocation) => {
  if (navLocation === "marketplace" && cart?.host?.handle) {
    return `store/${cart.host.handle}`;
  }

  return storePath || "home";
};

const getPaymentItemProduct = (item) => {
  const checkSum =
    item?.checkSum ||
    item?.productCheckSum ||
    item?.productNo ||
    item?.product?.checkSum;

  if (!item?.name || !checkSum) return null;

  return {
    ...item,
    ...(item.product || {}),
    name: item.name,
    checkSum,
  };
};

const formatVehicleType = (vehicleType) => {
  if (!vehicleType) return "Delivery";
  return vehicleType
    .toString()
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (value) => value.toUpperCase());
};

export const createCartCheckoutComponents = ({
  styles,
  UserLoginSwitch,
  PaymentMethod,
  renderCartItemMedia,
  enableOrderDetailsSync = false,
}) => {
const renderDefaultCartItemMedia = ({ item, imageLoaded, setImageLoaded }) => {
  if (!item.mediaThumb) {
    return (
      <div
        className={styles.abbrevatedDiv}
        style={{
          width: 85,
          height: 85,
          fontSize: "2.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f0f0f0",
          borderRadius: "0.25rem",
          marginRight: "0.5rem",
          flexShrink: 0,
        }}
        title={item.name}
      >
        {generateAbbreviation(item.name)}
      </div>
    );
  }

  return (
    <div
      style={{
        width: 85,
        height: 85,
        position: "relative",
        marginRight: "0.5rem",
        flexShrink: 0,
      }}
    >
      {!imageLoaded && <Skeleton width={85} height={85} />}
      {imageLoaded && (
        <img
          src={item.mediaThumb.replace("r200x200", "r300x300")}
          alt={item.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "0.25rem",
            display: "block",
          }}
        />
      )}
      <img
        src={item.mediaThumb.replace("r200x200", "r300x300")}
        alt=""
        style={{ display: "none" }}
        onLoad={() => setImageLoaded(true)}
      />
    </div>
  );
};

const CartOrderDetails = ({
  cart,
  accessPoint,
  title = "Order Details",
  showBillingDetails = false,
  paymentMethod,
  paymentChargeData,
  isLoadingCharge = false,
  onModifyCart,
  storePath,
  navLocation,
  showProduct,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const { navigateTo } = useHistoryManager(storePath);
  const disableStoreProductPrice =
    accessPoint?.settings?.disableStoreFrontPrice || false;
  const cartItems = {
    ...cart,
    paymentItem: cart?.paymentItem ?? [],
  };
  const currency =
    cartItems.currency || cartItems.paymentItem[0]?.currency || cart?.currency;
  const orderTotal = showBillingDetails
    ? cartItems.totalAmount ?? cartItems.amount ?? calculateCartTotal(cartItems)
    : cartItems.amount ?? calculateCartTotal(cartItems);
  const hostName = getCartSourceLabel(cartItems);
  const hostImage = cartItems.host?.image;
  const storeAddress =
    accessPoint?.address ||
    cartItems.accessPoint?.address ||
    cartItems.pickUpAddress ||
    cartItems.deliveryInfo?.pickUpAddress ||
    cartItems.host?.address ||
    "";
  const gatewayFee =
    paymentChargeData &&
    paymentChargeData.transactionChargeBearer === "USER" &&
    paymentMethod === "ONLINE"
      ? (paymentChargeData.processingFee || 0) +
        (paymentChargeData.addTransactionCharge
          ? paymentChargeData.toCharge || 0
          : 0)
      : 0;

  if (!cartItems.paymentItem.length) return null;

  const hostStorePath = getCartHostStorePath(cartItems, storePath, navLocation);
  const canNavigateToHost = Boolean(hostStorePath);

  const navigateToHost = () => {
    if (!canNavigateToHost) return;
    navigateTo(hostStorePath, {}, false);
  };

  const navigateToPaymentItem = (item) => {
    const product = getPaymentItemProduct(item);

    if (product?.checkSum) {
      const productStorePath = getCartHostStorePath(
        cartItems,
        storePath,
        navLocation,
      );
      navigateTo(generateProductUrl(productStorePath, product), {}, false);
      return;
    }

    if (typeof showProduct === "function") {
      showProduct(item);
    }
  };

  const handleKeyNavigation = (event, callback) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      callback();
    }
  };

  return (
    <div className={styles.cartOrderDetails}>
      <div
        className={styles.cartOrderHost}
        onClick={navigateToHost}
        onKeyDown={(event) => handleKeyNavigation(event, navigateToHost)}
        role={canNavigateToHost ? "button" : undefined}
        tabIndex={canNavigateToHost ? 0 : undefined}
      >
        <div className={styles.cartOrderHostHeader}>
          <div className={styles.cartOrderHostLogo}>
            {hostImage ? (
              <img src={hostImage} alt={`${hostName} logo`} />
            ) : (
              <span>{generateAbbreviation(hostName)}</span>
            )}
          </div>
          <div className={styles.cartOrderHostInfo}>
            <span>Store</span>
            <h3>{hostName}</h3>
          </div>
        </div>
        {storeAddress && (
          <p className={styles.cartOrderHostAddress}>
            <span>Store Owner Address:</span> {storeAddress}
          </p>
        )}
      </div>
      <hr className={styles.cartOrderHostDivider} />

      <div className={styles.cartOrderDetailsHeader}>
        <h2>
          {title}
          {/* {showBillingDetails && cartItems.checkSum && (
            <span> (Order No: {cartItems.checkSum})</span>
          )} */}
        </h2>
        {onModifyCart && (
          <button
            type="button"
            className={styles.checkoutSecondaryButton}
            onClick={onModifyCart}
          >
            Modify Cart
          </button>
        )}
      </div>
      <div className={cx(styles.cartItemsContainer, styles.cartItemsContainerReadonly)}>
        {cartItems.paymentItem.map((item) => (
          <div
            key={item._id}
            className={styles.cartItem}
            onClick={() => navigateToPaymentItem(item)}
            onKeyDown={(event) =>
              handleKeyNavigation(event, () => navigateToPaymentItem(item))
            }
            role="button"
            tabIndex={0}
          >
            <div className={styles.cartItemContent}>
              <div className={styles.cartInfoImg}>
                {item.mediaThumb ? (
                  <img
                    src={item.mediaThumb.replace("r200x200", "r300x300")}
                    alt={item.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "0.25rem",
                      display: "block",
                    }}
                  />
                ) : (
                  <div className={styles.abbrevatedDiv} title={item.name}>
                    {generateAbbreviation(item.name)}
                  </div>
                )}
              </div>

              <div className={styles.cartItemsInfo}>
                <div className={styles.cartItemHeader}>
                  <div className={styles.variantDisplay}>
                    <h3>
                      {item.name} X {item.unit}
                    </h3>
                    {item.isVariant &&
                      item.variant &&
                      item.node === "CHILD" && (
                        <div className={styles.variantItem}>
                          <span className={styles.variantInfo}>
                            {item.variant.hasColour && (
                              <span>Color: {item.variant.colorName}</span>
                            )}
                            {item.variant.hasColour && item.variant.hasSize && (
                              <span> &nbsp;|&nbsp; </span>
                            )}
                            {item.variant.hasSize && (
                              <span>Size: {item.variant.size}</span>
                            )}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
                <p className={styles.cartPrice}>
                  {disableStoreProductPrice
                    ? "Contact Seller for Price"
                    : `Price: ${formatCurrency(item.amount, item.currency)}`}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.cartOrderTotals}>
        {showBillingDetails ? (
          disableStoreProductPrice ? (
            <div className={cx(styles.summaryLine, styles.summaryTotal)}>
              <strong>Total:</strong>
              <div className={styles.negotiableMessage}>
                <strong>Price to Be Confirmed</strong>
                <p className={styles.smallText}>
                  You can place your order, seller will contact you to finalize
                  the price.
                </p>
              </div>
            </div>
          ) : (
            <>
              {isLoadingCharge && (
                <div className={cx(styles.summaryLine, styles.loadingLine)}>
                  <span>Loading processing fees...</span>
                  <span className="only-spinner" />
                </div>
              )}

              <div className={styles.summaryLine}>
                <span>Subtotal:</span>
                <span>{formatCurrency(cartItems.amount, currency)}</span>
              </div>

              {cartItems.vatAmount > 0 && (
                <div className={styles.summaryLine}>
                  <span>
                    VAT ({cartItems.vatPercentage}%
                    {cartItems.vatIncludedInProductPrice
                      ? ", Included in Product"
                      : ""}
                    ):
                  </span>
                  <span>{formatCurrency(cartItems.vatAmount, currency)}</span>
                </div>
              )}

              {cartItems.discountAmount > 0 && (
                <div className={styles.summaryLine}>
                  <span>Discount:</span>
                  <span>
                    -{formatCurrency(cartItems.discountAmount, currency)}
                    {cartItems.discountPercentage
                      ? ` (${cartItems.discountPercentage}%)`
                      : ""}
                  </span>
                </div>
              )}

              {cartItems.deliveryAmount > 0 && (
                <div className={styles.summaryLine}>
                  <span>Delivery Fee:</span>
                  <span>
                    {formatCurrency(cartItems.deliveryAmount, currency)}
                  </span>
                </div>
              )}

              {gatewayFee > 0 && (
                <div className={styles.summaryLine}>
                  <span>Payment Processing Fee:</span>
                  <span>
                    {formatCurrency(
                      gatewayFee,
                      paymentChargeData.currency || currency,
                    )}
                  </span>
                </div>
              )}

              <hr />

              <div className={cx(styles.summaryLine, styles.summaryTotal)}>
                <strong>Total:</strong>
                <strong>
                  {formatCurrency(
                    (cartItems?.totalAmount ?? 0) + (gatewayFee ?? 0),
                    currency,
                  )}
                </strong>
              </div>
            </>
          )
        ) : (
          !disableStoreProductPrice && (
            <div className={styles.cartTotal}>
              <h2>Total: {formatCurrency(orderTotal, currency)}</h2>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const CartItem = ({
  item,
  accessPointId,
  handleControlClick,
  showProduct,
  reportInvalidItem,
  showControls,
  disableStoreProductPrice,
  storePath,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const [product, setProduct] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const { isLoading, mutate: mututateGetProduct } = useGetProduct({
    onError: (response) => {
      setError(response.message);
    },
    onSuccess: async (response) => {
      setProduct(response.data.product);
    },
  });

  useEffect(() => {
    if (item && item.productId && !product) {
      mututateGetProduct({
        productId: item.productId,
        accessPointId,
      });
    }
  }, [item.productId, product]);

  useEffect(() => {
    if (product) {
      const isOutOfStock = !product.unlimited && product.sku <= 0;
      const isAtMax = !product.unlimited && item.unit > product.sku;

      const isInvalid = isOutOfStock || isAtMax;
      reportInvalidItem(item.productId, isInvalid);
    }
  }, [product, item.unit]);

  const viewProduct = () => {
    if (storePath === "market-place") {
      return;
    }
    if (product) {
      showProduct(product);
    }
  };

  const isOutOfStock = !product?.unlimited && product?.sku <= 0;
  const isAtMax = !product?.unlimited && item.unit > product?.sku;
  return (
    <>
      <div key={item._id} className={styles.cartItem}>
        <div className={styles.cartBox}>
          <div className={styles.cartItemHeader}>
            <div className={styles.variantDisplay}>
              <h3 onClick={() => viewProduct()}>{item.name}</h3>
              {item.isVariant && item.variant && item.node === "CHILD" && (
                <div className={styles.variantItem}>
                  <span className={styles.variantInfo}>
                    {item.variant.hasColour && (
                      <span>Color: {item.variant.colorName}</span>
                    )}
                    {item.variant.hasColour && item.variant.hasSize && (
                      <span> &nbsp;|&nbsp; </span>
                    )}
                    {item.variant.hasSize && (
                      <span>Size: {item.variant.size}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className={styles.cartItemContent}>
            <div className={styles.cartInfoImg} onClick={() => viewProduct()}>
              {(renderCartItemMedia || renderDefaultCartItemMedia)({
                item,
                imageLoaded,
                setImageLoaded,
              })}
            </div>

            <div className={styles.cartItemsInfo}>
              <div className={styles.cartBtns}>
                <div className="cart-info-case">
                  {showControls && (
                    <div className={styles.quantityControls}>
                      <button
                        onClick={() =>
                          handleControlClick("decrease", item._id, product)
                        }
                        disabled={item.unit <= 1 || isLoading}
                      >
                        -
                      </button>

                      <span
                        title={
                          isAtMax || isOutOfStock
                            ? "No more stock available"
                            : ""
                        }
                        className={
                          isAtMax || isOutOfStock ? styles.quantityAlert : ""
                        }
                      >
                        {" "}
                        {item.unit}{" "}
                      </span>

                      <button
                        onClick={() =>
                          handleControlClick("increase", item._id, product)
                        }
                        disabled={
                          (!product?.unlimited && product?.sku <= item.unit) ||
                          isLoading
                        }
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
                {showControls && (
                  <div className={styles.cartRemoveBox}>
                    <button
                      onClick={() =>
                        handleControlClick("remove", item._id, product)
                      }
                      className={styles.cartItemRemove}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <div className="cart-details-info">
                {!showControls && <p className={styles.cartQty}>Qty: {item.unit}</p>}
                <p className={styles.cartPrice}>
                  {disableStoreProductPrice
                    ? "Contact Seller for Price"
                    : ` Price: ${formatCurrency(item.amount, item.currency)}`}
                </p>

                {isOutOfStock && (
                  <div className={styles.outOfStockWarning}>
                    This product is currently out of stock.
                  </div>
                )}

                {!isOutOfStock && isAtMax && (
                  <div className={styles.cartWarningText}>
                    You&#39;ve exceeded the maximum available quantity.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CartSummary = ({
  cart,
  onProceed,
  accessPoint,
  setError,
  gotoDelivery,
  gotoContact,
  disableStoreProductPrice,
  paymentGateway,
  setPaymentGateway,
  setShowOrderDetails,
  navLocation,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const marketplaceOnlinePayment = navLocation === "marketplace";
  const disableOnlinePayment =
    disableStoreProductPrice && !marketplaceOnlinePayment;
  const [paymentChargeData, setPaymentChargeData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(
    disableOnlinePayment ? "NO_PAYMENT" : "ONLINE",
  );

  useEffect(() => {
    return () => {
      setShowOrderDetails?.(false);
    };
  }, [setShowOrderDetails]);

  useEffect(() => {
    if (marketplaceOnlinePayment) {
      setPaymentMethod("ONLINE");
      return;
    }

    if (disableOnlinePayment) {
      setPaymentMethod("NO_PAYMENT");
    }
  }, [disableOnlinePayment, marketplaceOnlinePayment]);

  useEffect(() => {
    if (disableOnlinePayment || paymentMethod !== "ONLINE") {
      setPaymentChargeData(null);
      return;
    }

    if (cart) {
      const orderData = {
        collectionId: cart.collectionId,
        mode: "ONLINE",
        gateWay: paymentGateway,
        channel: "WEB",
        amount: cart.totalAmount,
      };
      mutateInitializePaymentCharge(orderData);
    }
  }, [
    disableOnlinePayment,
    paymentMethod,
    paymentGateway,
    cart?.collectionId,
    cart?.totalAmount,
  ]);

  const { isLoading: isLoadingCharge, mutate: mutateInitializePaymentCharge } =
    useCartPaymentCharge({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        setPaymentChargeData(response.data);
      },
    });

  useEffect(() => {
    setShowOrderDetails?.({
      show: true,
      showBillingDetails: true,
      cart,
      paymentMethod,
      paymentGateway,
      paymentChargeData,
      isLoadingCharge,
    });
  }, [
    setShowOrderDetails,
    cart,
    paymentMethod,
    paymentGateway,
    paymentChargeData,
    isLoadingCharge,
  ]);

  if (!cart || !cart.paymentItem || cart.paymentItem.length === 0) {
    return (
      <div className={styles.cartSummary}>
        <div className={styles.emptyCartMessage}>Your cart is empty.</div>
      </div>
    );
  }

  // Fallbacks and mappings
  const contact = cart.publicOrder ? cart.publicInfo : cart.author;
  const routeDeliveryEstimate = cart.deliveryInfo?.routeDeliveryEstimate;
  const deliveryVehicleType =
    routeDeliveryEstimate?.vehicleType || cart?.vehicleType;
  const deliveryFee = Number(
    routeDeliveryEstimate?.deliveryFee || cart?.deliveryAmount || 0,
  );
  const deliveryCurrency =
    routeDeliveryEstimate?.currency ||
    cart?.currency ||
    cart?.paymentItem?.[0]?.currency;
  const vendorNote =
    typeof cart.deliveryInfo?.note === "string" && cart.deliveryInfo.note.trim()
      ? cart.deliveryInfo.note
      : "";

  const EditHeader = ({ label, onEdit }) => (
    <div className={styles.summaryHeader}>
      <h3>{label}</h3>
      <span className={styles.summaryHeaderEdit} onClick={onEdit}>
        Edit
      </span>
    </div>
  );

  return (
    <>
      <div className={styles.cartSummary}>
        {/* Contact Info */}

        <EditHeader label="Contact Information:" onEdit={gotoContact} />
        <div className={styles.summaryLine}>
          <span>Name:</span>
          <span>{cart.contactName || "N/A"}</span>
        </div>
        <div className={styles.summaryLine}>
          <span>Phone:</span>
          {contact?.phone ? (
            <span>
              {contact?.intCode
                ? `${contact.intCode} ${contact.phone}`
                : contact.phone}
            </span>
          ) : (
            <span>N/A</span>
          )}
        </div>
        <div className={styles.summaryLine}>
          <span>Email:</span>
          <span>{contact?.email || "N/A"}</span>
        </div>

        {/* Delivery Information */}
        {cart.deliveryInfo && cart.deliveryMode !== "INSTORE" && (
          <>
            <hr />

            <EditHeader
              label={
                cart.deliveryMode === "PICKUP"
                  ? "Pickup Address"
                  : "Delivery Information"
              }
              onEdit={gotoDelivery}
            />

            {cart.deliveryMode === "LOCATION" ? (
              <>
                <div className={styles.summaryLine}>
                  <span>Address:</span>
                  <span>{cart.deliveryInfo?.deliveryAddress || "N/A"}</span>
                </div>
                {cart.deliveryInfo.deliveryRegion && (
                  <div className={styles.summaryLine}>
                    <span>Region:</span>
                    <span>{cart.deliveryInfo.deliveryRegion}</span>
                  </div>
                )}
                {(routeDeliveryEstimate || deliveryVehicleType || deliveryFee > 0) && (
                  <>
                    {deliveryVehicleType && (
                      <div className={styles.summaryLine}>
                        <span>Vehicle:</span>
                        <span>{formatVehicleType(deliveryVehicleType)}</span>
                      </div>
                    )}
                    {deliveryFee > 0 && (
                      <div className={styles.summaryLine}>
                        <span>Delivery fee:</span>
                        <span>{formatCurrency(deliveryFee, deliveryCurrency)}</span>
                      </div>
                    )}
                    {routeDeliveryEstimate && (
                      <div className={styles.summaryLine}>
                        <span>Fee status:</span>
                        <span>
                          {cart.deliveryInfo.routeDeliveryEstimateApplied
                            ? "Added to order"
                            : "Estimate only"}
                        </span>
                      </div>
                    )}
                    {(routeDeliveryEstimate?.estimatedDelivery ||
                      routeDeliveryEstimate?.distance?.km) && (
                      <div className={styles.summaryLine}>
                        <span>Delivery estimate:</span>
                        <span>
                          {routeDeliveryEstimate.estimatedDelivery ||
                            "Estimated delivery"}
                          {routeDeliveryEstimate.distance?.km
                            ? ` - ${Number(
                                routeDeliveryEstimate.distance.km,
                              ).toFixed(1)} km`
                            : ""}
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className={styles.summaryLine}>
                  <span>Phone:</span>
                  <span>
                    {cart.deliveryInfo?.intCode
                      ? `${cart.deliveryInfo.intCode} ${cart.deliveryInfo.phone}`
                      : cart.deliveryInfo?.phone || "N/A"}
                  </span>
                </div>
                {vendorNote && (
                  <div className={styles.summaryLine}>
                    <span>Vendor note:</span>
                    <span>{vendorNote}</span>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.summaryLine}>
                <span>Pickup Address:</span>
                <span>{cart.deliveryInfo.pickUpAddress}</span>
              </div>
            )}
          </>
        )}

        {cart.deliveryMode === "INSTORE" && (
          <>
            <hr />
            <EditHeader label="Collection Mode" onEdit={gotoDelivery} />
            <div className={styles.summaryLine}>
              <span>Mode:</span>
              <span>In-store Purchase</span>
            </div>
          </>
        )}
      </div>
      <div className={styles.paymentMethodSection}>
        <PaymentMethod
          cart={cart}
          accessPoint={accessPoint}
          onProceed={onProceed}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          disableStoreProductPrice={disableStoreProductPrice}
          paymentGateway={paymentGateway}
          setPaymentGateway={setPaymentGateway}
          navLocation={navLocation}
        />
      </div>
    </>
  );
};

const CartCheckout = ({
  cart,
  error,
  setError,
  handleLoginClick,
  handleCartSubmit,
  showProduct,
  modal,
  closeModal,
  cartProcessLoading,
  activateBackButton,
  confirmation,
  onProceed,
  accessPoint,
  gotoDelivery,
  gotoContact,
  isPageMode,
  storePath,
  organisation,
  navLocation,
  invalidateCollections,
  setPopUp,
  startSwitch,
  showMarketplaceCheckoutAction = true,
  paymentGateway,
  setPaymentGateway,
  setShowOrderDetails,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const disableStoreProductPrice =
    navLocation === "marketplace"
      ? false
      : accessPoint?.settings?.disableStoreFrontPrice || false;

  const dispatch = useAppDispatch();
  const cartSource = useAppSelector(getCartSource);
  useEffect(() => {
    activateBackButton(false);
  }, []);

  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );

  const [cartItems, setCartItems] = useState({
    ...cart,
    paymentItem: cart?.paymentItem ?? [],
  });
  const updateRequestRef = useRef(0);
  const hasPendingCartUpdateRef = useRef(false);

  const { navigateTo } = useHistoryManager(storePath);

  const debouncedCartItems = useDebounce(cartItems, 1000); // Debounce with 500ms delay

  const [invalidItems, setInvalidItems] = useState([]);
  const pushLocalCartUpdate = useCallback(
    (nextCart) => {
      const organisationId = getCartOrganisationId(nextCart);
      const accessPointId = nextCart?.accessPointId;

      if (organisationId && accessPointId) {
        dispatch(updateCart(organisationId, accessPointId, nextCart));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (cart) {
      setCartItems(cart);
    }
  }, [cart?.collectionId]);

  useEffect(() => {
    if (!enableOrderDetailsSync) return undefined;

    setShowOrderDetails?.({
      show: true,
      showBillingDetails: false,
      cart: cartItems,
      paymentMethod: null,
      paymentGateway: null,
      paymentChargeData: null,
      isLoadingCharge: false,
    });

    return () => {
      setShowOrderDetails?.(false);
    };
  }, [setShowOrderDetails, cartItems]);

  const { isLoading, mutate: mututateUpdateCart } = useUpdateActiveCart({});

  const syncCartUpdate = useCallback(
    (nextCart) => {
      const requestId = updateRequestRef.current + 1;
      updateRequestRef.current = requestId;

      mututateUpdateCart(
        {
          orgId: getCartOrganisationId(nextCart),
          accessPointId: nextCart?.accessPointId,
          paymentItem: nextCart?.paymentItem ?? [],
          cartSource: cartSource,
        },
        {
          onError: (response) => {
            if (requestId !== updateRequestRef.current) return;
            setError(response.message);
            const cart = response.data.collection;
            if (cart) {
              dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
            }
            invalidateCollections();
          },
          onSuccess: async (response) => {
            if (requestId !== updateRequestRef.current) return;
            const cart = response.data.collection;
            dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
            setCartItems(cart);
            toast.success("Cart Updated");
            invalidateCollections();
          },
        },
      );
    },
    [cartSource, dispatch, invalidateCollections, mututateUpdateCart, setError],
  );

  useEffect(() => {
    if (hasPendingCartUpdateRef.current) {
      hasPendingCartUpdateRef.current = false;
      syncCartUpdate(debouncedCartItems);
    }
  }, [debouncedCartItems]);

  const handleControlClick = useCallback((action, itemId, product) => {
    if (!product) return;
    setError("");
    switch (action) {
      case "increase":
        increaseQuantity(itemId, product);
        break;
      case "decrease":
        decreaseQuantity(itemId);
        break;
      case "remove":
        removeItem(itemId, product);
        break;
      default:
        break;
    }
  }, []);

  const increaseQuantity = (itemId, product) => {
    if (!product.unlimited && product.sku <= 0) {
      toast.error(`${product.name} is out of stock.`);
      return;
    }
    setCartItems((prevItems) => {
      const paymentItem = prevItems.paymentItem.map((item) =>
        item._id === itemId && (product.unlimited || item.unit < product.sku)
          ? { ...item, unit: item.unit + 1 }
          : item,
      );
      const nextCart = getCartWithPaymentItems(prevItems, paymentItem);
      hasPendingCartUpdateRef.current = true;
      pushLocalCartUpdate(nextCart);
      return nextCart;
    });
  };

  const decreaseQuantity = (itemId) => {
    setCartItems((prevItems) => {
      const paymentItem = prevItems.paymentItem.map((item) =>
        item._id === itemId && item.unit > 1
          ? { ...item, unit: item.unit - 1 }
          : item,
      );
      const nextCart = getCartWithPaymentItems(prevItems, paymentItem);
      hasPendingCartUpdateRef.current = true;
      pushLocalCartUpdate(nextCart);
      return nextCart;
    });
  };

  const removeItem = (itemId, product) => {
    setCartItems((prevItems) => {
      const updatedItems = prevItems.paymentItem.filter(
        (item) => item._id !== itemId,
      );

      // Remove from invalidItems using productId
      if (product?.productId) {
        setInvalidItems((prev) =>
          prev.filter((id) => id !== product.productId),
        );
      }

      const nextCart = getCartWithPaymentItems(prevItems, updatedItems);
      pushLocalCartUpdate(nextCart);
      syncCartUpdate(nextCart);
      return nextCart;
    });
  };

  const reportInvalidItem = (productId, isInvalid) => {
    setInvalidItems((prev) => {
      const alreadyInvalid = prev.includes(productId);
      if (isInvalid && !alreadyInvalid) return [...prev, productId];
      if (!isInvalid && alreadyInvalid)
        return prev.filter((id) => id !== productId);
      return prev;
    });
  };

  const currency = cartItems.currency;

  return (
    <div>
      <div
        className={cx(
          styles.cartHeader,
          navLocation === "marketplace" && styles.marketplace,
        )}
      >
        {cart && (
          <div>
            <h3>Items in Your Cart from {getCartSourceLabel(cart)}</h3>
          </div>
        )}
        {!isPageMode && !loggedInUser?.user && (
          <UserLoginSwitch handleLoginClick={handleLoginClick} />
        )}
      </div>

      {(cartItems.paymentItem ?? []).length > 0 ? (
        <>
          <div className={cx(styles.cartBox, styles.showBoth)}>
            <div className={styles.cartLeftSection}>
              <div className={styles.cartItemsContainer}>
                {cartItems.paymentItem.map((item) => (
                  <CartItem
                    key={item._id}
                    item={item}
                    accessPointId={cart.accessPointId}
                    handleControlClick={handleControlClick}
                    showProduct={showProduct}
                    isPageMode={isPageMode}
                    reportInvalidItem={reportInvalidItem}
                    showControls={!confirmation}
                    disableStoreProductPrice={disableStoreProductPrice}
                    setPopUp={setPopUp}
                    orgId={organisation?.orgId}
                    profileId={cartItems.host?.profileId}
                    storePath={storePath}
                  />
                ))}
              </div>
              {error && <div className={styles.errorMessage}>{error}</div>}
              {invalidItems.length > 0 && (
                <div className={styles.errorMessage}>
                  Some items in your cart are out of stock or exceeded available
                  quantity
                </div>
              )}

              {!confirmation && (
                <>
                  {!disableStoreProductPrice && (
                    <div className={styles.cartTotal}>
                      <h3>
                        Total:{" "}
                        {formatCurrency(calculateCartTotal(cartItems), currency)}
                      </h3>
                    </div>
                  )}
                  {storePath !== "market-place" && (
                    <div className={styles.checkoutBox}>
                      <button
                        className={styles.checkoutButton}
                        onClick={handleCartSubmit}
                        disabled={cartProcessLoading || invalidItems.length > 0}
                      >
                        {cartProcessLoading ? "Checkin Out..." : "Checkout"}
                      </button>
                      {!isPageMode ? (
                        <button
                          className={styles.checkoutSecondaryButton}
                          onClick={() =>
                            modal ? closeModal("continue") : navigateTo("home")
                          }
                        >
                          Continue shopping
                        </button>
                      ) : (
                        <button
                          className={styles.checkoutSecondaryButton}
                          onClick={() => navigateTo(storePath)}
                        >
                          Continue shopping
                        </button>
                      )}
                    </div>
                  )}
                  {storePath === "market-place" &&
                    showMarketplaceCheckoutAction && (
                      <div className={styles.checkoutBox}>
                        <button
                          className={styles.checkoutButton}
                          onClick={() => {
                            startSwitch(cart);
                          }}
                          // disabled={cartProcessLoading || invalidItems.length > 0}
                        >
                          Checkout from {cart.host?.name}
                        </button>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className={styles.emptyCartMessage}>
          <span>{cartIcon}</span>
          <h3>Your cart is empty.</h3>
          <p>Looks like you haven&apos;t added anything to your cart yet.</p>
          <button
            className={styles.checkoutSecondaryButton}
            onClick={() =>
              modal ? closeModal("continue") : navigateTo("home")
            }
          >
            Continue shopping
          </button>
        </div>
      )}
    </div>
  );
};

return {
  CartCheckout,
  CartOrderDetails,
  CartSummary,
};
};
